import { GetListingAddOnAvailabilityPublicEndpoint } from '@kouto/types';
import { useRef } from 'react';
import useSWR from 'swr';
import { get } from 'utils/axios';
import { convertIntervalToMinutes } from 'utils/formatDuration';
import { z } from 'zod';

type Availability = z.infer<
  typeof GetListingAddOnAvailabilityPublicEndpoint.responseSchema
>;

const AVAILABILITY_FRESHNESS_MS = 2 * 60 * 1000;

interface Session {
  listingId?: string;
  experienceId?: string;
  startDateTime?: string;
  sessionDuration?: string | number;
  hasAddOns?: boolean;
}

const useBookableAddOns = ({
  listingId,
  experienceId,
  startDateTime,
  sessionDuration,
  hasAddOns,
}: Session) => {
  const duration =
    typeof sessionDuration === 'string'
      ? convertIntervalToMinutes(sessionDuration)
      : sessionDuration ?? 0;
  const getAvailabilityUrl = (dateTime?: string) => {
    if (!listingId || !experienceId) return null;
    if (!dateTime || duration <= 0) return null;

    return GetListingAddOnAvailabilityPublicEndpoint.url({
      listingId,
      query: {
        experienceId,
        startDateTime: dateTime,
        sessionDuration: duration,
      },
    });
  };
  const url = hasAddOns ? getAvailabilityUrl(startDateTime) : null;
  const lastFetch = useRef<{ url: string; at: number } | null>(null);
  const { data, isValidating } = useSWR<Availability>(url, get<Availability>, {
    onSuccess: (_response, requestUrl) => {
      lastFetch.current = { url: requestUrl, at: Date.now() };
    },
  });

  const hasBookableAddOns = async (
    selectedStartDateTime = startDateTime,
  ): Promise<boolean> => {
    const selectedUrl = hasAddOns
      ? getAvailabilityUrl(selectedStartDateTime)
      : null;
    if (!selectedUrl) return true;

    try {
      // The submitted session may differ from the prefetched selection (for example, a deep link).
      const isFresh =
        lastFetch.current?.url === selectedUrl &&
        Date.now() - lastFetch.current.at < AVAILABILITY_FRESHNESS_MS;
      const result =
        selectedUrl === url && data && !isValidating && isFresh
          ? data
          : await get<Availability>(selectedUrl);
      return (
        result.items.length === 0 || result.items.some((item) => item.available)
      );
    } catch {
      return true;
    }
  };

  return hasBookableAddOns;
};

export default useBookableAddOns;
